// lib/services/compute/animation_pool.ts

import TypedSlotPool, { PostReleasePolicy } from "./typed_slot_pool";
import AnimationSlot from "./animation_slot";

const SLOT_COUNT = 3;

/**
 * Three-slot pool of per-frame GPU animation data.
 *
 * Producer (AnimationControllerGpu compute encoding) leases an Idle slot,
 * fills its animation_state_buffer, dispatches the compute pass that
 * writes the joint_palette_buffer, then marks the slot Ready.
 *
 * Consumer (SkinnedRenderPipeline) leases the latest Ready slot, binds
 * its joint_palette_buffer to the render pass, then releases. The slot
 * stays Ready and may be re-leased next frame if no newer slot has
 * been published.
 */
export default class AnimationPool {
  private pool: TypedSlotPool<AnimationSlot>;
  private slots: AnimationSlot[] = [];
  private initialized = false;

  private node_count = 0;
  private joint_count = 0;

  private sequence_counter = 0;

  constructor() {
    this.pool = new TypedSlotPool<AnimationSlot>(PostReleasePolicy.KeepReady);
  }

  initialize(
    device: GPUDevice,
    key: string,
    node_count: number,
    joint_count: number
  ): boolean {
    if (this.initialized) return true;

    if (node_count <= 0 || joint_count <= 0) {
      throw new Error(
        `AnimationPool.initialize: node_count (${node_count}) and joint_count (${joint_count}) must be positive.`
      );
    }

    this.node_count = node_count;
    this.joint_count = joint_count;

    this.slots = [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = new AnimationSlot();
      slot.allocate_buffers(device, key, i, joint_count);
      this.slots.push(slot);
    }

    this.pool.initialize(this.slots);
    this.initialized = true;
    return true;
  }

  shutdown(): void {
    for (const slot of this.slots) {
      slot.destroy_buffers();
    }
    this.pool.shutdown();
    this.slots = [];
    this.node_count = 0;
    this.joint_count = 0;
    this.sequence_counter = 0;
    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Producer-side: find an Idle slot to fill. The caller transitions
   * the slot through begin_record() -> ... -> mark_ready(). The pool
   * does not auto-publish; the caller decides when the slot is Ready.
   */
  acquire_idle_slot(): AnimationSlot | null {
    return this.pool.acquire_idle_slot();
  }

  /**
   * Producer-side helper: assign the next monotonic sequence and timestamp,
   * mark the slot Ready, and recycle any older Ready slots that are not
   * currently leased. Single call instead of three.
   */
  publish_slot(slot: AnimationSlot, timestampSec: number): void {
    this.sequence_counter += 1;
    this.pool.publish_slot(slot, this.sequence_counter, timestampSec);
  }

  /**
   * Producer-side: cancel an in-progress write, returning the slot to Idle.
   */
  cancel_slot(slot: AnimationSlot): void {
    slot.cancel_record();
    this.pool.cancel_slot(slot);
  }

  /**
   * Consumer-side: lease the most recent Ready slot. Returns null if
   * no slot has been published yet or if a lease is already held.
   */
  acquire_latest_ready_slot(): AnimationSlot | null {
    return this.pool.acquire_latest_ready_slot();
  }

  /**
   * Consumer-side: release the currently leased slot. The slot stays
   * Ready (KeepReady policy) and may be re-leased if no newer slot
   * has arrived.
   */
  release_latest_ready_slot(): void {
    this.pool.release_leased_slot();
  }

  getNodeCount(): number {
    return this.node_count;
  }

  getJointCount(): number {
    return this.joint_count;
  }

  getSlotCount(): number {
    return this.pool.getSlotCount();
  }

  getIdleCount(): number {
    return this.pool.getIdleCount();
  }

  getLatestPublishedSequence(): number {
    return this.pool.getLatestPublishedSequence();
  }

  getLatestConsumedSequence(): number {
    return this.pool.getLatestConsumedSequence();
  }

  getSupersededCount(): number {
    return this.pool.getSupersededCount();
  }
}
